import React, { Component, ReactNode } from 'react';
import ComponentName from '../../../constant/ComponentName';
import ItemToolsMenu from '../../ItemToolsMenu';
import { query } from '@trlogic/client-js';
import _ from 'lodash';
import {
  CustomFilter as FilterType,
  CustomFilterCategorical,
  CustomFilterDateSearch,
  CustomFilterFields,
  CustomFilterLiteralSearch,
} from '../../../domain/ui/FilterDomains';
import FilterUtil from '../../../util/FilterUtil';
import { dispatch, RootState } from '../../../redux/store';
import { changePageFilter } from '../../../redux/actions/PageActions';
import ValueFactory from '../../../util/ValueFactory';
import AuditService from '../../../service/AuditService';
import ServiceContainer from '../../../config/ServiceContainer';
import { ServiceNames } from '../../../constant/ServiceNames';
import CustomTable, { CustomColumnProps } from '../../custom/CustomTable';
import CustomTableContentWrapper from '../../custom/CustomTableContentWrapper';
import moment from 'moment/moment';
import CustomPaginator, { CustomPaginatorProps } from '../../custom/CustomPaginator';
import CustomLoading from '../../custom/CustomLoading';
import { LoginEventsResponseDto } from '../../../domain/audit/KeycloakAuditLogDomain';
import { LoginAuditsExportJob } from '../../../domain/job/JobDomain';
import MessageUtil from '../../../util/MessageUtil';
import JobService from '../../../service/JobService';
import { DataTableSortMeta } from 'primereact/datatable';
import DataType from '../../../constant/DataType';
import { PageState } from '../../../redux/reducers/PageReducer';
import { connect } from 'react-redux';
import { changeCaseColumns, changeListDataLoading } from '../../../redux/actions/CaseActions';
import { setAttribute } from '../../../redux/actions/UserActions';
import { ConnectedProps } from 'react-redux/es/exports';
import Filter = query.Filter;
import empty = query.empty;
import Order = query.Order;
import LanguageUtil from '../../../util/LanguageUtil';

class LoginAuditsComponent extends Component<Props, State> {
  public state = new State();

  private auditService: AuditService = ServiceContainer.getInstance().get(ServiceNames.AUDIT);
  private readonly jobService: JobService = ServiceContainer.getInstance().get(ServiceNames.JOB);

  public render(): ReactNode {
    const { filter, audits, rows, currentPage, loading, totalRecord } = this.state;
    return (
      <div className={'flex flex-col'}>
        <ItemToolsMenu
          onChangeFilter={this.onChangeFilter}
          filter={filter}
          exportVisible
          exportVisibleAlways
          importVisible={false}
          exportFunc={() => this.onExport()}
          defaultFilterDisabled={true}
          component={ComponentName.LOGIN_AUDIT_LOG}
          componentLabel={LanguageUtil.get({ id: 'pages.admin.audit.login.title', defaultMessage: 'Login Audit Logs' })}
        />
        <div className={'flex w-full h-full max-h-[calc(100dvh-360px)] overflow-scroll'}>
          {loading ? (
            <CustomLoading />
          ) : (
            <CustomTable columns={this.getColumns()} onSortChange={this.onSortChange} multiSortMeta={this.getOrder()} value={audits} />
          )}
          <CustomPaginator
            rows={rows}
            totalRecords={totalRecord}
            rowsPerPageOptions={[5, 10, 20, 50]}
            onPageChange={this.onPageChange}
            currentPage={currentPage}
          />
        </div>
      </div>
    );
  }

  public async componentDidMount() {
    this.prepareFilterFields();
    const count = await this.auditService.getKeycloakLoginEventsCount(this.state.filter);
    this.setState({ totalRecord: count }, this.getAudits);
  }

  private prepareFilterFields = async () => {
    const fields: CustomFilterFields[] = await FilterUtil.getFields(ComponentName.LOGIN_AUDIT_LOG);
    dispatch(
      changePageFilter({
        filter: ValueFactory.initializeFilter(),
        items: fields,
      }),
    );
  };

  private onChangeFilter = async (filter: Filter) => {
    this.setState({ loading: true });
    const isSame = _.isEqual(filter, this.state.filter);
    if (isSame) return;
    const count = await this.auditService.getKeycloakLoginEventsCount(filter);
    this.setState({ totalRecord: count, filter }, this.getAudits);
  };

  private getAudits = async () => {
    const { rows, currentPage, sorts, filter } = this.state;
    this.setState({ loading: true });
    const st = currentPage - 1;
    const audits = await this.auditService.getKeycloakLoginEvents(rows * st, rows, filter, sorts);
    this.setState({ audits, loading: false });
  };

  public onPageChange = (event: CustomPaginatorProps) => {
    this.setState({ rows: event.rows, currentPage: event.page }, this.getAudits);
  };

  protected onSortChange = (sorts: Order[]) => {
    this.setState({ sorts }, this.getAudits);
  };

  private getOrder = (): DataTableSortMeta[] => {
    const { sorts } = this.state;
    return sorts.map((s) => {
      return {
        field: s.field,
        order: s.operator == 'ASC' ? 1 : -1,
      } as DataTableSortMeta;
    });
  };

  private getColumns = (): CustomColumnProps[] => {
    return [
      {
        field: 'username',
        header: LanguageUtil.get({ id: 'common.label.username', defaultMessage: 'Username' }),
        alignHeader: 'left',
        className: 'w-1/4',
        filterable: true,
        copyable: true,
        onCopy: (e: LoginEventsResponseDto) => {
          return e.userId;
        },
        addFilter: (e: LoginEventsResponseDto) => {
          this.addFilter('user_id', e.userId, 'Username', DataType.STRING, true);
        },
        body: (record: LoginEventsResponseDto) => {
          return (
            <div className={'flex items-start text-xs 2xl:text-md'}>
              <CustomTableContentWrapper
                text={record.details?.username || LanguageUtil.get({ id: 'pages.admin.audit.fields.username', defaultMessage: 'Username' })}
              />
            </div>
          );
        },
      },
      {
        field: 'type',
        header: LanguageUtil.get({ id: 'common.label.action', defaultMessage: 'Action' }),
        filterable: true,
        copyable: true,
        onCopy: (e: LoginEventsResponseDto) => {
          return e.type;
        },
        addFilter: (e: LoginEventsResponseDto) => {
          this.addFilter('type', e.type, 'Type', DataType.STRING, true);
        },
        body: (record: LoginEventsResponseDto) => {
          const loginType = this.handleLoginType(record.type);
          return (
            <div className={'text-xs 2xl:text-md'}>
              <CustomTableContentWrapper text={loginType} />
            </div>
          );
        },
      },
      {
        field: 'time',
        header: LanguageUtil.get({ id: 'common.label.date', defaultMessage: 'Date' }),
        filterable: true,
        copyable: true,
        onCopy: (e: LoginEventsResponseDto) => {
          return moment(e.time).format('LLLL').valueOf();
        },
        addFilter: (e: LoginEventsResponseDto) => {
          this.addFilter('event_time', e.time, 'Date', DataType.DATE);
        },
        body: (record: LoginEventsResponseDto) => {
          return (
            <div className={'text-xs 2xl:text-md'}>
              <CustomTableContentWrapper text={moment(record.time).format('LLLL').valueOf()} />
            </div>
          );
        },
      },
    ];
  };

  private addFilter = async (field: string, value: any, label: string, dataType: DataType, categorical: boolean = false) => {
    const { changePageFilter, page } = this.props;
    const filter: { filter: FilterType; items: CustomFilterFields[] } = page.filter;
    if (categorical) {
      filter.filter.fields.push({
        field: field,
        label: label,
        enabled: true,
        search: {
          type: 'CATEGORICAL',
          value: {
            values: [value],
          } as CustomFilterCategorical,
        },
      });
    } else {
      switch (dataType) {
        case DataType.STRING:
        case DataType.NUMBER:
          filter.filter.fields.push({
            field: field,
            label: label,
            enabled: true,
            search: {
              type: 'LITERAL',
              value: {
                properties: {
                  dataType: dataType,
                },
                operator: 'eq',
                value: value,
              } as CustomFilterLiteralSearch,
            },
          });
          break;
        case DataType.DATE:
          filter.filter.fields.push({
            field: field,
            label: label,
            enabled: true,
            search: {
              type: 'DATE',
              value: {
                startDate: {
                  label: moment(value).format('LLLL'),
                  value: value,
                  type: 'Absolute',
                },
                endDate: {
                  label: moment(value).format('LLLL'),
                  value: value,
                  type: 'Absolute',
                },
              } as CustomFilterDateSearch,
            },
          });
          break;
        case DataType.BOOLEAN:
          await this.addFilter(field, value, label, dataType, true);
          break;
        default:
          return;
      }
    }
    changePageFilter(filter);
    this.onChangeFilter(this.parseFilter());
  };

  private parseFilter = () => {
    const { page } = this.props;
    const {
      filter: { filter },
    }: { filter: { filter: FilterType } } = page;
    return FilterUtil.parse(filter);
  };

  private handleLoginType = (type: string) => {
    switch (type) {
      case 'LOGIN':
        return LanguageUtil.get({ id: 'messages.success.successfulLogin' });
      case 'LOGOUT':
        return LanguageUtil.get({ id: 'messages.success.successfulLogout' });
      case 'LOGIN_ERROR':
        return LanguageUtil.get({ id: 'messages.failed.failedLogin' });
      case 'LOGOUT_ERROR':
        return LanguageUtil.get({ id: 'messages.failed.failedLogout' });
      default:
        return type;
    }
  };
  private async onExport() {
    try {
      const job: LoginAuditsExportJob = {
        type: 'LOGIN_AUDITS_EXPORT',
        timestamp: moment().valueOf(),
        status: 'PENDING',
        filter: this.state.filter,
        extension: 'csv',
        path: '',
      };
      await this.jobService.schedule(job);
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'common.label.export', defaultMessage: 'Export' }),
        detail: <div>{LanguageUtil.get({ id: 'messages.success.exportScheduled', defaultMessage: 'Export job scheduled' })},</div>,
      });
    } catch (e) {
      console.error(e);
    }
  }
}

class State {
  loading: boolean = true;
  filter: Filter = empty();
  audits: any[] = [];
  rows: number = 10;
  currentPage: number = 1;
  totalRecord: number = 0;
  sorts: Order[] = [{ field: 'time', operator: 'DESC' }];
}

interface Props extends ReduxProps {}

const mapStateToProps = (state: RootState) => ({
  page: state.page as PageState,
});

const connector = connect(mapStateToProps, { changeCaseColumns, setAttribute, changeListDataLoading, changePageFilter });
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(LoginAuditsComponent);
